import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const STORAGE_KEY = 'pb_verification_mode';

const VerificationContext = createContext(null);

export function VerificationProvider({ children }) {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  const [hiddenCount, setHiddenCount] = useState(0);

  const reportedIds = useRef(new Set());
  const tickRef = useRef(0);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0'); } catch {}
    reportedIds.current = new Set();
    setHiddenCount(0);
  }, [enabled]);

  const reportHidden = useCallback((id) => {
    if (!id) return;
    if (reportedIds.current.has(id)) return;
    reportedIds.current.add(id);
    tickRef.current += 1;
    setHiddenCount(reportedIds.current.size);
  }, []);

  const reportShown = useCallback((id) => {
    if (!id) return;
    if (!reportedIds.current.has(id)) return;
    reportedIds.current.delete(id);
    setHiddenCount(reportedIds.current.size);
  }, []);

  const toggle = useCallback(() => setEnabled(v => !v), []);

  const value = useMemo(() => ({
    enabled, toggle, setEnabled, hiddenCount, reportHidden, reportShown,
  }), [enabled, toggle, hiddenCount, reportHidden, reportShown]);

  return (
    <VerificationContext.Provider value={value}>
      {children}
    </VerificationContext.Provider>
  );
}

export function useVerification() {
  const ctx = useContext(VerificationContext);
  if (!ctx) {
    return { enabled: false, toggle: () => {}, setEnabled: () => {}, hiddenCount: 0, reportHidden: () => {}, reportShown: () => {} };
  }
  return ctx;
}
